// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Lustre — the bootstrap. Owns the screens and the room, and no game logic whatsoever.
//
// The room is joined ONCE and held until the player goes back to the menu. Every round after the
// first — every "play again" — happens inside it, versioned by rematch.ts. Leaving and re-joining to
// "reset" hands back the room object Trystero is still tearing down, and every player then sits in
// the right room code, alone.

import '@ben-gy/game-engine/mobile.css';
import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createNet, roomAppId, setTurnConfig, type Net, type PeerId } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type Rounds } from '@ben-gy/game-engine/rematch';
import {
  clearRoomInUrl, createLobby, createRoomEntry, normalizeRoomCode, setRoomInUrl, type Lobby,
} from '@ben-gy/game-engine/lobby';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { DEFAULT_MODE, MODES, MODE_IDS, modeOf, type ModeId } from './modes';
import { actingSeat, type PlyEvent } from './rules';
import { Match, seatForRound, type WireMsg } from './match';
import { resolveSel, refusalFor } from './pick';
import { View, type Sel } from './view';
import { runCountdown } from './countdown';
import { FOOTER_HTML, HOW_HTML, aboutHtml, menuHtml, openPanel, rivalHtml, summaryHtml } from './ui';
import './styles/main.css';

const SLUG = 'lustre';

const app = document.getElementById('app')!;
const store = createStore(SLUG);
const sfx = createSfx({
  muted: store.get<boolean>('muted', false) ?? false,
  patches: {
    tap: { type: 'square', freq: [520, 520], dur: 0.03, gain: 0.09 },
    take: { type: 'triangle', freq: [380, 520], dur: 0.06, gain: 0.12 },
    claim: { type: 'triangle', freq: [420, 700], dur: 0.1, gain: 0.16 },
    cut: { type: 'sine', freq: [700, 1240], dur: 0.24, gain: 0.24 },
    order: { type: 'triangle', freq: [640, 1160], dur: 0.26, gain: 0.22 },
    nope: { type: 'square', freq: [190, 120], dur: 0.13, gain: 0.15 },
    rise: { type: 'sine', freq: [300, 240], dur: 0.16, gain: 0.14 },
    tick: { type: 'triangle', freq: [660, 660], dur: 0.08, gain: 0.16 },
    go: { type: 'triangle', freq: [880, 1320], dur: 0.22, gain: 0.22 },
    over: { type: 'triangle', freq: [560, 320], dur: 0.5, gain: 0.2 },
  },
});
const play = (n: string): void => {
  try {
    sfx.play(n);
  } catch {
    /* audio is decoration; never let it take the game down */
  }
};

let mode: ModeId = modeOf(store.get<string>('mode', DEFAULT_MODE)).id;

interface RoundOpts { mode: string }

let net: Net | null = null;
let rounds: Rounds<RoundOpts> | null = null;
let lobby: Lobby | null = null;
let match: Match | null = null;
let view: View | null = null;
let roomCode = '';
let iMintedIt = false;
let beat: ReturnType<typeof setInterval> | null = null;
let clock: ReturnType<typeof setInterval> | null = null;
let ticker: ReturnType<typeof setInterval> | null = null;
let countdown: { cancel: () => void } | null = null;
let deepLinkUsed = false;
let sendWire: (m: WireMsg) => void = () => {};
let sel: Sel = null;
let names: string[] = [];
let seatOf = new Map<PeerId, number>();

const setPlaying = (on: boolean): void => {
  document.body.classList.toggle('playing', on);
};

function clearScreen(): void {
  countdown?.cancel();
  countdown = null;
  view?.destroy();
  view = null;
  for (const t of [clock, ticker]) if (t) clearInterval(t);
  clock = null;
  ticker = null;
  document.removeEventListener('keydown', onKey);
  app.replaceChildren();
}

function onKey(e: KeyboardEvent): void {
  if (!view) return;
  if ((e.target as HTMLElement)?.tagName === 'INPUT') return;
  if (view.handleKey(e)) e.preventDefault();
}

// ── screens ───────────────────────────────────────────────────────────────────────────────────

function showMenu(): void {
  clearScreen();
  setPlaying(false);
  app.innerHTML = menuHtml(mode, sfx.muted(), store.get<number>(`best:${mode}`, 0) ?? 0);

  app.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((b) =>
    b.addEventListener('click', () => {
      mode = modeOf(b.dataset.mode).id;
      store.set('mode', mode);
      showMenu();
    }),
  );
  app.querySelector('#playSolo')!.addEventListener('click', () => {
    sfx.unlock();
    play('tap');
    startSolo();
  });
  app.querySelector('#playFriends')!.addEventListener('click', () => {
    sfx.unlock();
    play('tap');
    showRoomEntry();
  });
  app.querySelector('#howBtn')!.addEventListener('click', () => openPanel(app, HOW_HTML));
  app.querySelector('#aboutBtn')!.addEventListener('click', () => openPanel(app, aboutHtml()));
  const mute = app.querySelector<HTMLButtonElement>('#muteBtn')!;
  mute.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    mute.textContent = sfx.muted() ? 'Sound off' : 'Sound on';
    mute.setAttribute('aria-pressed', String(sfx.muted()));
  });

  if (!store.get<boolean>('seenHow', false)) {
    store.set('seenHow', true);
    openPanel(app, HOW_HTML);
  }
}

/** Solo is instant: seated at the wheel against two bots, on the first frame, with no lobby. */
function startSolo(): void {
  names = ['You', 'Marl', 'Verity'];
  buildMatch({ mode, seed: (Math.random() * 0xffffffff) >>> 0, round: 1, n: 3, mySeat: 0, bot: true });
}

// ── the room ──────────────────────────────────────────────────────────────────────────────────

function showRoomEntry(): void {
  clearScreen();
  setPlaying(false);
  const host = document.createElement('div');
  host.className = 'main-content';
  app.append(host);
  createRoomEntry({
    container: host,
    title: 'Play with friends',
    subtitle: 'Start a room and send the link — or scan the code, or type it in.',
    onSubmit: (code, created) => {
      roomCode = normalizeRoomCode(code);
      iMintedIt = created;
      enterRoom();
    },
    onCancel: () => showMenu(),
  });
  app.insertAdjacentHTML('beforeend', FOOTER_HTML);
}

function enterRoom(): void {
  clearScreen();
  setRoomInUrl(roomCode);
  net = createNet(
    { appId: roomAppId(SLUG), roomId: roomCode, claimHost: iMintedIt },
    {
      // NEVER navigate from a net handler: these fire in the lobby AND just after a round ends.
      onPeers: () => {
        lobby?.repaint();
        match?.peerBack();
      },
      onPeerLeave: (id: PeerId) => {
        const seat = seatOf.get(id);
        if (seat !== undefined) match?.peerLeft(seat);
        lobby?.repaint();
      },
      onHostChange: () => lobby?.repaint(),
    },
  );

  const wire = net.channel<WireMsg>('lus', (m) => match?.receive(m));
  sendWire = (m) => wire(m);

  rounds = createRounds<RoundOpts>({
    net,
    playerName: store.get<string>('name', '') || `Cutter ${Math.floor(Math.random() * 90 + 10)}`,
    minPlayers: 2,
    roundOpts: () => ({ mode }),
    onRound: (info) => {
      // The HOST's pick is what the room plays, frozen inside the start, so two peers can never be
      // working different boards on the same seed.
      const m = modeOf(info.opts?.mode).id;
      if (!info.seated) {
        showSpectator();
        return;
      }
      const n = Math.max(2, Math.min(4, info.players.length));
      seatOf = new Map();
      names = new Array<string>(n).fill('');
      info.players.slice(0, n).forEach((pl, idx) => {
        const st = seatForRound(idx, info.round, n);
        seatOf.set(pl.id, st);
        names[st] = pl.name;
      });
      startRound({
        mode: m,
        seed: info.seed,
        round: info.round,
        n,
        mySeat: seatOf.get(net!.selfId) ?? 0,
        bot: false,
      });
    },
    onChange: () => lobby?.repaint(),
  });

  showLobby();
}

function showLobby(): void {
  clearScreen();
  setPlaying(false);
  const host = document.createElement('div');
  host.className = 'main-content';
  app.append(host);
  lobby = createLobby({
    container: host,
    net: net!,
    rounds: rounds!,
    roomCode,
    minPlayers: 2,
    maxPlayers: 4,
    modeSlot: () => {
      const st = rounds!.state();
      // Guests see the HOST's gossiped choice. Rendering your own pick and labelling it the host's
      // is a confident lie, and it has shipped in this fleet before.
      const chosen = st.isHost ? mode : modeOf(st.hostOpts?.mode).id;
      return `<div class="modeslot"><span class="modeslot-label">Mode</span>
        <div class="segmented small">${MODE_IDS.map(
          (id) =>
            `<button type="button" class="seg${id === chosen ? ' on' : ''}" data-lmode="${id}"${
              st.isHost ? '' : ' disabled'
            }>${MODES[id].name}</button>`,
        ).join('')}</div>
        <p class="fine">${st.isHost ? MODES[mode].blurb : 'The host picks the mode.'}</p></div>`;
    },
    onModeMount: () => {
      host.querySelectorAll<HTMLButtonElement>('[data-lmode]').forEach((b) =>
        b.addEventListener('click', () => {
          mode = modeOf(b.dataset.lmode).id;
          store.set('mode', mode);
          lobby?.repaint();
        }),
      );
    },
    onCancel: () => leaveRoom(),
  });
  app.insertAdjacentHTML('beforeend', FOOTER_HTML);
}

function showSpectator(): void {
  clearScreen();
  setPlaying(false);
  app.innerHTML = `<div class="main-content"><h2>A round is already under way</h2>
    <p class="fine">You are in the next one. Hang on — it will start on its own.</p></div>${FOOTER_HTML}`;
}

/**
 * Leaving is the ONLY place the room is torn down, and it clears `?room=` on the way out. Leave it
 * in the URL and a reload — or reopening from a home-screen icon — silently drags the player back
 * into a room they left, with no way to start a new one.
 */
function leaveRoom(): void {
  if (beat) clearInterval(beat);
  beat = null;
  match = null;
  rounds?.destroy();
  rounds = null;
  lobby?.destroy();
  lobby = null;
  void net?.leave();
  net = null;
  clearRoomInUrl();
  deepLinkUsed = true;
  showMenu();
}

// ── a round ───────────────────────────────────────────────────────────────────────────────────

interface StartArgs {
  mode: ModeId;
  seed: number;
  round: number;
  n: number;
  mySeat: number;
  bot: boolean;
}

function startRound(args: StartArgs): void {
  clearScreen();
  setPlaying(true);
  const holder = document.createElement('div');
  holder.className = 'countwrap';
  app.append(holder);
  countdown = runCountdown({
    container: holder,
    sfx: play,
    onDone: () => {
      holder.remove();
      buildMatch(args);
    },
  });
}

function buildMatch(args: StartArgs): void {
  clearScreen();
  setPlaying(true);
  sel = null;

  match = new Match({
    modeId: args.mode,
    seed: args.seed,
    round: args.round,
    names,
    n: args.n,
    mySeat: args.mySeat,
    botTier: args.bot ? 'cutter' : null,
    deps: {
      send: (m) => sendWire(m),
      onChange: () => repaint(),
      onEvents: (evts) => hear(evts),
      onEnd: () => showResults(),
    },
  });

  view = new View({
    onSelect: (s) => {
      sel = s;
      if (s) play('tap');
      repaint();
    },
    onCommit: () => commit(),
    onHelp: () => openPanel(app, HOW_HTML),
    onRival: (seat) => {
      if (match) openPanel(app, rivalHtml(match.state, seat, names[seat] ?? `Seat ${seat + 1}`));
    },
    onQuit: () => {
      // Back to the lobby, never out of the room — and rounds.finish() FIRST, because the engine
      // lobby paints nothing at all while the round is still 'playing' and this seat is seated.
      if (net) {
        rounds?.finish();
        showLobby();
      } else showMenu();
    },
  });
  app.append(view.root);
  document.addEventListener('keydown', onKey);

  // setInterval, never rAF alone: a backgrounded tab must still fill in for a seat that has gone
  // quiet, and rAF simply does not fire there.
  if (clock) clearInterval(clock);
  clock = setInterval(() => match?.tick(250), 250);
  if (beat) clearInterval(beat);
  if (!args.bot) beat = setInterval(() => match?.broadcast(), 2000);
  match.broadcast();
  repaint();
}

function commit(): void {
  const m = match;
  if (!m || !view) return;
  const a = resolveSel(m.state, m.mySeat ?? 0, sel);
  if (a < 0) {
    play('nope');
    return;
  }
  const priceBefore = m.state.dial;
  if (!m.submit(a)) {
    play('nope');
    return;
  }
  void priceBefore;
  sel = null;
  repaint();
}

function hear(evts: PlyEvent[]): void {
  for (const e of evts) {
    if (e.kind === 'cut') play('cut');
    else if (e.kind === 'patron') play('order');
    else if (e.kind === 'claim') play('claim');
    else if (e.kind === 'gather' || e.kind === 'pan') play('take');
  }
}

function repaint(): void {
  const m = match;
  if (!view || !m) return;
  const mySeat = m.mySeat ?? 0;
  const armed = resolveSel(m.state, mySeat, sel);
  const turn = actingSeat(m.state);
  view.render({
    state: m.state,
    mySeat,
    names,
    sel,
    armed,
    reason: armed >= 0 ? '' : refusalFor(m.state, mySeat, sel),
    waiting: turn < 0 ? 'The wheel has stopped' : `${names[turn] ?? `Seat ${turn + 1}`} is deciding…`,
    fault:
      m.fault === 'build'
        ? 'Someone is on a different version — reload'
        : m.fault === 'desync'
          ? 'The tables disagree — reload'
          : '',
    away: m.missing,
  });
}

// ── the end ───────────────────────────────────────────────────────────────────────────────────

function showResults(): void {
  play('over');
  setPlaying(false);
  const m = match;
  if (!m) return;
  const rows = m.standings();
  const mySeat = m.mySeat ?? 0;
  if (!net) {
    const mine = rows.find((r) => r.seat === mySeat);
    const best = store.get<number>(`best:${m.mode.id}`, 0) ?? 0;
    if (mine && mine.total > best) store.set(`best:${m.mode.id}`, mine.total);
  }
  clearScreen();
  app.innerHTML = summaryHtml(rows, names, mySeat, m.mode.id, '');

  const again = app.querySelector<HTMLButtonElement>('#againBtn')!;
  const toLobby = app.querySelector<HTMLButtonElement>('#lobbyBtn')!;
  const wait = app.querySelector<HTMLElement>('#resWait')!;

  if (net && rounds) {
    rounds.finish();
    toLobby.hidden = false;
    toLobby.addEventListener('click', () => showLobby());
    again.addEventListener('click', () => {
      rounds!.vote();
      again.disabled = true;
      again.textContent = 'Waiting for the others…';
    });
    // A visible reason and a horizon. One player still reading the table must not hold the room.
    ticker = setInterval(() => {
      const st = rounds?.state();
      if (!st) return;
      wait.hidden = false;
      wait.textContent =
        st.startsInMs !== null
          ? `Next round in ${Math.ceil(st.startsInMs / 1000)}s…`
          : `${st.votes.length} of ${st.present.length} ready.`;
    }, 400);
  } else {
    again.addEventListener('click', () => {
      play('tap');
      startSolo();
    });
  }
  app.querySelector('#menuBtn')!.addEventListener('click', () => (net ? leaveRoom() : showMenu()));
  app
    .querySelector('.results-feedback')!
    .addEventListener('click', (e) => window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }));
}

// ── boot ──────────────────────────────────────────────────────────────────────────────────────

async function boot(): Promise<void> {
  hardenViewport();
  // ONCE, before any mesh exists. Trystero builds one global connection pool from the first room's
  // config, so a later call leaves the initiating half of every pair STUN-only.
  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fails open — TURN is an upgrade, never a dependency */
  }

  // A `?room=` is honoured ONCE. After that, "play with friends" always offers create-or-join, so a
  // reload can never drag somebody back into a room they have left.
  const deep = new URL(location.href).searchParams.get('room');
  if (deep && !deepLinkUsed) {
    deepLinkUsed = true;
    roomCode = normalizeRoomCode(deep);
    iMintedIt = false;
    enterRoom();
    return;
  }
  showMenu();
}

window.addEventListener('beforeunload', () => void net?.leave());
void boot();

// Exposed for the browser verification pass: drives a round synchronously in a tab where rAF and
// real timers are throttled. Never used by the game itself.
(window as unknown as Record<string, unknown>).__lustre = {
  state: () =>
    match && {
      mode: match.mode.id,
      ply: match.state.ply,
      turn: actingSeat(match.state),
      mySeat: match.mySeat,
      dial: match.state.dial,
      over: match.over,
      totals: match.standings().map((r) => r.total),
      close: match.state.close,
      pawns: match.state.players.map((p) => p.pawn),
    },
  legal: () => (match ? resolveSel(match.state, match.mySeat ?? 0, sel) : -1),
  select: (s: Sel) => {
    sel = s;
    repaint();
  },
  commit: () => commit(),
  /** Play the whole round by taking the shipped bot's move for every seat, including mine. */
  auto: (n = 400) => {
    for (let k = 0; k < n && match && !match.over; k++) {
      const seat = actingSeat(match.state);
      if (seat < 0) break;
      if (!match.playFor(seat)) break;
    }
    return match?.over;
  },
  modes: MODE_IDS,
};
